import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  ExportDropdown,
  Hours,
  InternalClientTooltip,
  Percent,
  ProjectLink,
  RouteLink,
  Widget,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  ReportPeriodFilter,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useClientFilters,
  useDocumentTitle,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTaskFilters,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const Member = styled.div`
  display: flex;
  align-items: center;
  font-weight: ${weights.bold};
`;

const ProjectInfo = styled.div`
  margin-left: 1rem;
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Task = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
`;

const NoTask = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
  color: ${colors.grey25};
`;

export default function TimeSummaryByMemberAndProjectAndTask({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const memberFilters = useMemberFilters();
  const timeFilters = useTimeFilters();
  const taskFilters = useTaskFilters();

  const [params, setParams] = useState({
    period: null,
    sort: new QuerySort('member.name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...memberFilters.filters,
    ...roleFilters.filters,
    ...timeFilters.filters,
    ...taskFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        sort: { default: new QuerySort('member.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        roleFilters,
        timeFilters,
        taskFilters,
      ],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, roleFilters, timeFilters, taskFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);

    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, urlSearchParams, report.id]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions report={report} onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter value={params.period} onChange={({ value }) => handleApplyFilters({ period: value })} />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />

        <TaskFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <>
      <Report.Summary>
        <Widget>
          <Meter>
            <Meter.Value
              label="Client Billable"
              value={report.totals.billable}
              color={colors.success}
              tooltip={
                <>
                  <Hours value={report.totals.billable} /> hours (
                  <Percent value={report.totals.billable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Client Non-Billable"
              value={report.totals.nonBillable}
              color={colors.danger}
              tooltip={
                <>
                  <Hours value={report.totals.nonBillable} /> hours (
                  <Percent value={report.totals.nonBillable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Internal"
              value={report.totals.internal}
              color={colors.warning}
              tooltip={
                <>
                  <Hours value={report.totals.internal} /> hours (
                  <Percent value={report.totals.internal / report.totals.total} />)
                </>
              }
            />
          </Meter>

          <Metrics>
            <Metric>
              <Metric.Value>
                <Hours value={report.totals.billable} />
              </Metric.Value>
              <Metric.Label>Client Billable</Metric.Label>
            </Metric>
            <Metric>
              <Metric.Value>
                <Hours value={report.totals.nonBillable} />
              </Metric.Value>
              <Metric.Label>Client Non-Billable</Metric.Label>
            </Metric>
            <Metric>
              <Metric.Value>
                <Hours value={report.totals.internal} />
              </Metric.Value>
              <Metric.Label>Internal</Metric.Label>
            </Metric>
            <Metric>
              <Metric.Value>
                <Hours value={report.totals.total} />
              </Metric.Value>
              <Metric.Label>Total</Metric.Label>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="member.name" onSort={onSort} sort={params.sort}>
              Member
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Client
              <br />
              Billable
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Client
              <br />
              Non-Billable
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Internal
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((member) => {
              return (
                <React.Fragment key={member.id}>
                  <Table.GroupRow top="4rem">
                    <Table.Cell>
                      <Member>{member.name}</Member>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'billable' })}>
                          <Hours value={member.billableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'non_billable' })}>
                          <Hours value={member.nonBillableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'internal' })}>
                          <Hours value={member.internalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink
                          to={timeDetail({
                            memberId: member.id,
                            timeBillableTypeId: ['billable', 'non_billable', 'internal'],
                          })}>
                          <Hours value={member.totalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell />
                    <Table.Cell />
                  </Table.GroupRow>
                  {member.projects.map((project) => {
                    return (
                      <React.Fragment key={project.id}>
                        <Table.Row>
                          <Table.Cell>
                            <ProjectInfo>
                              <p>
                                <ProjectLink project={project} />
                              </p>
                              <small>
                                <ClientLink client={project.client} />
                                {project.client.isInternal && <InternalClientTooltip />}
                              </small>
                            </ProjectInfo>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink
                                to={timeDetail({
                                  memberId: member.id,
                                  projectId: project.id,
                                  timeBillableTypeId: 'billable',
                                })}>
                                <Hours value={project.billableHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink
                                to={timeDetail({
                                  memberId: member.id,
                                  projectId: project.id,
                                  timeBillableTypeId: 'non_billable',
                                })}>
                                <Hours value={project.nonBillableHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink
                                to={timeDetail({
                                  memberId: member.id,
                                  projectId: project.id,
                                  timeBillableTypeId: 'internal',
                                })}>
                                <Hours value={project.internalHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink to={timeDetail({ memberId: member.id, projectId: project.id })}>
                                <Hours value={project.totalHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell />
                          <Table.Cell />
                          <Table.Cell />
                        </Table.Row>
                        {project.tasks?.map((task) => {
                          if (!task.id)
                            return (
                              <Table.Row key="no-task-row">
                                <Table.Cell>
                                  <NoTask>{task.name}</NoTask>
                                </Table.Cell>
                                <Table.Cell>
                                  <Hours value={task.billableHours} />
                                </Table.Cell>
                                <Table.Cell>
                                  <Hours value={task.nonBillableHours} />
                                </Table.Cell>
                                <Table.Cell>
                                  <Hours value={task.internalHours} />
                                </Table.Cell>
                                <Table.Cell>
                                  <Hours value={task.totalHours} />
                                </Table.Cell>
                                <Table.Cell />
                                <Table.Cell />
                                <Table.Cell />
                              </Table.Row>
                            );
                          return (
                            <Table.Row key={task.id}>
                              <Table.Cell>
                                <Task>{task.name}</Task>
                              </Table.Cell>
                              <Table.Cell>
                                <RouteLink
                                  to={timeDetail({
                                    memberId: member.id,
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    timeBillableTypeId: 'billable',
                                  })}>
                                  <Hours value={task.billableHours} />
                                </RouteLink>
                              </Table.Cell>
                              <Table.Cell>
                                <RouteLink
                                  to={timeDetail({
                                    memberId: member.id,
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    timeBillableTypeId: 'non_billable',
                                  })}>
                                  <Hours value={task.nonBillableHours} />
                                </RouteLink>
                              </Table.Cell>
                              <Table.Cell>
                                <RouteLink
                                  to={timeDetail({
                                    memberId: member.id,
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    timeBillableTypeId: 'internal',
                                  })}>
                                  <Hours value={task.internalHours} />
                                </RouteLink>
                              </Table.Cell>
                              <Table.Cell>
                                <RouteLink
                                  to={timeDetail({
                                    memberId: member.id,
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                  })}>
                                  <Hours value={task.totalHours} />
                                </RouteLink>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.billable} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.nonBillable} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.internal} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.total} />
              </Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell></Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Member"
            isLoading={query.status !== 'ready'}
            partialResults={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
